import { useCallback, useMemo } from "react";
import styled from "styled-components";
import useSWR from "swr";
import MrclText from "../../marcel/MrclText";
import { useTranslation } from "react-i18next";
import { MagasinOption, PerformanceView } from "../Performance.model";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import { useStores } from "../../../context/storesContext";
import { fetcherWithStoreTypeAndCountryKey} from "../../../utils/fetcher";
import { useModals } from "../../../context/modalsContext";
import { Store } from "../../../types/store";
import PerformanceFilterPanel from "../PerformanceFilterPanel/PerformanceFilterPanel";
import { usePerformance } from "../context/PerformanceContext";
import { getLabelDate } from "../../../utils/dateUtils";


const PerformanceHeader = () => {
  const { t } = useTranslation();
  const { isKpiExists } = useKpiVisibility();
  const { selectedStore } = useStores();
  const { showModal, closeModal } = useModals();
  const {
    view: activeView,
    setView: handleViewChange,
    selectedMagasin,
    setSelectedMagasin,
    isFilterOpen,
    setIsFilterOpen,
    analysisDate,
    isCALive,
    isPerformanceLoading,
  } = usePerformance();


  const { data: storeListData } = useSWR(
      () =>
          selectedStore?.storeType && selectedStore?.countryKey
              ? [`/stores/type`, selectedStore.storeType, selectedStore.countryKey]
              : null,
      ([url, storeType, countryKey]) =>
          fetcherWithStoreTypeAndCountryKey(url, countryKey, storeType)
  );

  const storeArray: Store[] = useMemo(() => {
    if (!storeListData) return [];
    return storeListData.filter((s: any) => s.storeKey !== selectedStore?.storeKey)
        .map((s: any) => ({
      storeName:               s.storeName ?? "",
      storeKey:                s.storeKey ?? "",
      storeAnabelKey:          s.storeAnabelKey ?? "",
      storeType:               s.storeType ?? "",
      affiliation:             s.affiliation ?? "",
      countryKey:              s.countryKey ?? "",
      leaseManagementTypeName: s.leaseManagementTypeName ?? "",
      openingTime:             s.openingTime,
      closingTime:             s.closingTime,
    } as Store));
  }, [storeListData, selectedStore?.storeKey]);

  const openMagasinSelector = useCallback(() => {
    showModal({
      modalToShow: "STORE_SELECTOR_MODAL",
      modalProps: {
        title: t("commercial.choisir_magasin_miroir") || "Choisissez un magasin miroir",
        onCancel: closeModal,
        storeArray,
        onSelect: (storeKey: string) => {
          const store = storeArray.find((s) => s.storeKey === storeKey);
          if (store) {
            const magasin: MagasinOption = { id: store.storeKey, label: store.storeName };
            setSelectedMagasin(magasin);
          }
          closeModal();
        },
        selectedStore: storeArray.find((s) => s.storeKey === selectedMagasin?.id) ?? null,
        mode: "store",
      },
    });
  }, [showModal, closeModal, storeArray, selectedMagasin, setSelectedMagasin, t]);

  const TITLE_MAP: Record<PerformanceView, string> = {
    horaire: t("commercial.performance_détaillée"),
    rayon:   t("commercial.performance_détaillée"),
    magasin: t("commercial.performance_détaillée"),
  };

  const TITLE_SHORT_MAP: Record<PerformanceView, string> = {
    horaire: t("commercial.performance_détaillée_short"),
    rayon:   t("commercial.performance_détaillée_short"),
    magasin: t("commercial.performance_détaillée_short"),
  };

  const VIEWS: { value: PerformanceView; label: string; icon: string; disabled?: boolean }[] = [
    { value: "rayon",   label: t("commercial.rayon"),   icon: "sidebar" },
    { value: "magasin", label: isCALive ? t("commercial.territoire") : t("commercial.magasin"), icon: isCALive ? "map" : "store" },
  ];

  if (isKpiExists("Commercial", "TurnoverByHour")) {
    VIEWS.unshift({ value: "horaire", label: t("commercial.horaire"), icon: "clock"});
  }

  const title = TITLE_MAP[activeView];
  const titleSeparatorIndex = title.indexOf(" - ");
  const titleLabel = titleSeparatorIndex === -1 ? title : title.slice(0, titleSeparatorIndex);
  const titleSuffix = titleSeparatorIndex === -1 ? "" : title.slice(titleSeparatorIndex);

  const shortTitle = TITLE_SHORT_MAP[activeView];
  const shortTitleSeparatorIndex = shortTitle.indexOf(" - ");
  const shortTitleLabel = shortTitleSeparatorIndex === -1 ? shortTitle : shortTitle.slice(0, shortTitleSeparatorIndex);

  return (
      <Wrapper $filterOpen={isFilterOpen}>
        <Header $filterOpen={isFilterOpen}>
          <Title>
            <TitleDefault>{titleLabel}</TitleDefault>
            <TitleMobileLandscape>{shortTitleLabel}</TitleMobileLandscape>
            {titleSuffix && <TitleSuffix>{titleSuffix}</TitleSuffix>}
            <HeaderTextSmall>{getLabelDate(analysisDate)}</HeaderTextSmall>
          </Title>
          <Controls>
            <TabGroup>
              {VIEWS.map(({ value, label, icon, disabled }) => {
                  // Determine if the tab should be disabled due to loading, but only if it's not the active tab
                  const isDisabledByLoading = isPerformanceLoading && activeView !== value;
                  const isEffectivelyDisabled = !!disabled || isDisabledByLoading;

                  return (
                      <TabButton
                          key={value}
                          type="button"
                          $active={activeView === value}
                          $disabled={isEffectivelyDisabled}
                          disabled={isEffectivelyDisabled}
                          onClick={() => !isEffectivelyDisabled && handleViewChange(value)}
                      >
                          <mrcl-icon icon={icon} size="m" />
                          {label}
                      </TabButton>
                  );
              })}
            </TabGroup>
            {!isCALive && <FilterIconWrapper>
              <mrcl-icon-button
                  icon="filter"
                  variant={!isFilterOpen ? "outlined" : "filled"}
                  tone="brand"
                  size="s"
                  aria-label="Filtrer"
                  disabled={isPerformanceLoading}
                  onClick={() => !isPerformanceLoading && setIsFilterOpen(prev => !prev)}
              />
            </FilterIconWrapper> }
          </Controls>
        </Header>
       
        {isFilterOpen && (
          <PerformanceFilterPanel
            onSelectClick={openMagasinSelector} 
          />
        )}
      </Wrapper>
  );
};

export default PerformanceHeader;

const FilterIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  button {
    width: 40px !important;
    height: 40px !important;
  }
`;


const Wrapper = styled.div<{ $filterOpen: boolean }>`
  width: 100%;
  border-radius: 8px;
  overflow: visible;
  background: #ffffff;
`;

const Header = styled.div<{ $filterOpen: boolean }>`
  height: 66px;
  border-radius: var(--border-radius-m, 8px);
  border: 1px solid var(--Persistent-Border-Default, #EBEBEB);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 12px;
  padding-left: 12px;
  background-color: #FFFFFF;
  box-shadow:
    0px 1px 2px -1px rgba(0, 0, 0, 0.1),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
`;
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TabGroup = styled.div`
  display: flex;
  min-width: 308px;
  height: 40px;
  border-radius: 4px;
  padding: 4px;
  gap: 4px;
  background: #f1f3f5;
  border: none;
`;

const TabButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  height: 32px;
  border-radius: 4px;
  padding-right: 16px;
  padding-left: 16px;
  border: none;
  background: ${({ $active, $disabled }) => 
    $active ? "#2563eb" : ($disabled ? "transparent" : "transparent")};
  color: ${({ $active, $disabled }) => 
    $active ? "#ffffff" : ($disabled ? "#c1c9d2" : "#374151")};
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  font-size: var(--font-size-body-s, 14px);
  line-height: var(--line-height-body-s, 20px);
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ $active, $disabled }) => 
    $disabled ? "transparent" : ($active ? "#1d4ed8" : "#e5e7eb")};
  }
`;

const HeaderTextSmall = styled.span`
  padding-left: 10px;
  font-weight: 400;
  font-size: var(--font-size-body-s, 14px);
  line-height: var(--line-height-body-s, 20px);
  letter-spacing: var(--letter-spacing-normal, normal);
  color: #696969;
`;

const TitleSuffix = styled.span`
  font-weight: 400;
`;


const Title = styled(MrclText)`
  font-weight: 700;
  font-size: var(--font-size-body-m, 16px);
  line-height: var(--line-height-body-m, 24px);
  letter-spacing: var(--letter-spacing-normal, normal);
  vertical-align: middle;
`;

const TitleDefault = styled.span`
  display: inline;

  @media only screen and (max-width: 932px) and (orientation: landscape),
    only screen and (max-height: 560px) and (orientation: landscape) {
    display: none;
  }
`;

const TitleMobileLandscape = styled.span`
  display: none;

  @media only screen and (max-width: 932px) and (orientation: landscape),
    only screen and (max-height: 560px) and (orientation: landscape) {
    display: inline;
  }
`;
